import { useState } from "react";
import { isGettingCode, isSuccess, PortInStep } from "../portInStep";
import { getVerificationCode } from "./getVerificationCodeApi";
import { PhoneNumberView } from "./PhoneNumberView";
import { validateMobileNumber } from "./validateMobileNumberApi";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { IMsisdn, INetwork } from "@interface/IPortinForm";
import { removeWhitespace } from "@utils/stringUtils";
import type { FieldName, FieldValues, UseFormMethods } from "react-hook-form";

interface IParams<TFieldValues extends FieldValues = FieldValues> {
    phoneNumberSelected: string;
    formMethods: UseFormMethods<TFieldValues>;
    hasTooManyAttempt: boolean;
    portInStep: PortInStep;
    previousPhoneNumber?: string;
    setIsFinished(newValue: boolean): void;
    updatePortInStep(nextStep: PortInStep): void;
    setCurrentProvider(currentProvider: INetwork): void;
    setHasTooManyAttempt(hasTooManyAttempt: boolean): void;
    setPhoneNumberSelected(phoneNumber: string): void;
    namePrefix?: string;
    clearCardError?(): void;
}

export const PhoneNumber = <TFieldValues extends FieldValues = FieldValues>(props: IParams<TFieldValues>) => {
    const {
        updatePortInStep,
        setIsFinished,
        portInStep,
        previousPhoneNumber,
        formMethods,
        setCurrentProvider,
        hasTooManyAttempt,
        setHasTooManyAttempt,
        setPhoneNumberSelected,
        phoneNumberSelected,
        namePrefix,
        clearCardError,
    } = props;

    const { t } = useTranslation(["common", "portin"]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const phoneNumberFieldName = namePrefix ? `${namePrefix}phoneNumber` : "phoneNumber";

    const changeValue = (newPhoneNumber: string) => {
        clearCardError?.();
        const cleanValue = removeWhitespace(newPhoneNumber);
        setHasTooManyAttempt(false);
        formMethods.clearErrors(phoneNumberFieldName as FieldName<TFieldValues>);
        formMethods.setValue(phoneNumberFieldName as FieldName<TFieldValues>, (cleanValue as unknown) as never);
    };

    const phoneNumberValue = useWatch<string>({
        name: phoneNumberFieldName,
        control: formMethods.control,
        defaultValue: "",
    });

    const getCode = async () => {
        setErrorMessage(undefined);
        if (await formMethods.trigger(phoneNumberFieldName as FieldName<TFieldValues>)) {
            if (previousPhoneNumber && previousPhoneNumber === phoneNumberValue) {
                updatePortInStep(PortInStep.successCode);
                setIsFinished(true);
                setPhoneNumberSelected(phoneNumberValue);
            } else {
                setIsLoading(true);

                validateMobileNumber(phoneNumberValue, setErrorMessage)
                    .then((result: IMsisdn) => {
                        if (result) {
                            if (result.isPortInProgressAlready) {
                                setErrorMessage(t("portin:portInInProgress"));
                                setIsLoading(false);
                            } else {
                                setCurrentProvider(result.network);
                                getOtpIdCode();
                            }
                        } else {
                            getOtpIdCode();
                        }
                    })
                    .catch(() => {
                        getOtpIdCode();
                    });
            }
        }
    };

    const getOtpIdCode = () => {
        getVerificationCode(phoneNumberValue, setErrorMessage)
            .then((response) => {
                if (response) {
                    updatePortInStep(PortInStep.verifyCode);
                    setPhoneNumberSelected(phoneNumberValue);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setErrorMessage(t("common:genericError"));
            });
    };

    return (
        <PhoneNumberView
            changeValue={changeValue}
            errorMessage={errorMessage}
            formMethods={formMethods}
            getCode={getCode}
            hasTooManyAttempt={hasTooManyAttempt}
            isGettingCode={isGettingCode(portInStep)}
            isLoading={isLoading}
            isSuccess={isSuccess(portInStep)}
            namePrefix={namePrefix}
            phoneNumberSelected={phoneNumberSelected}
            previousPhoneNumber={previousPhoneNumber}
        />
    );
};
